/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 381 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-12-07
 * Modified By:  Cursor
 *
 * Description:
 * Standardize toast usage via centralized notifications helper.
 * Enforce consistent UI messaging style and error sanitization.
 *
 * Related Screens / Modules:
 * - client/src/utils/notifications.ts
 * - client/src/features/inventory/EditItemModal.tsx
 */

import React from "react";
import { Modal, Row, Col, Alert } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { notify } from "../../utils/notifications";
import { InventoryItem, updateItem } from "../../services/adminInventory";

interface EditItemModalProps {
  show: boolean;
  item: InventoryItem | null;
  onHide: () => void;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  manufacturer: Yup.string()
    .required("Manufacturer is required")
    .min(2, "Manufacturer must be at least 2 characters"),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Quantity must be at least 1")
    .integer("Quantity must be a whole number"),
  price: Yup.number().nullable().min(0, "Price must be positive"),
  currency: Yup.string()
    .oneOf(["USD", "EUR", "GBP"], "Currency must be USD, EUR, or GBP")
    .required("Currency is required"),
  condition: Yup.string().required("Condition is required"),
  category: Yup.string(),
  description: Yup.string(),
  country: Yup.string(),
});

const EditItemModal: React.FC<EditItemModalProps> = ({ show, item, onHide, onSuccess }) => {
  const formik = useFormik({
    initialValues: {
      manufacturer: item?.manufacturer || "",
      partNumber: item?.partNumber || "",
      model: item?.model || "",
      title: item?.title || "",
      quantity: item?.quantity || 1,
      condition: item?.condition || "",
      price: item?.price || "",
      currency: item?.currency || "USD",
      category: item?.category || "",
      description: item?.description || "",
      country: item?.country || "",
      isActive: item?.isActive !== false,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        setStatus(null);

        const updateData = {
          manufacturer: values.manufacturer,
          partNumber: values.partNumber || undefined,
          model: values.model || undefined,
          title: values.title || undefined,
          quantity: values.quantity,
          condition: values.condition,
          price: values.price ? Number(values.price) : null,
          currency: values.currency,
          category: values.category || undefined,
          description: values.description || undefined,
          country: values.country || undefined,
          isActive: values.isActive,
        };

        await updateItem(item!._id, updateData);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Your changes have been saved");
        onSuccess();
      } catch (error: any) {
        console.error("Update error:", error);
        const errorMessage = error.response?.data?.message || "We couldn't update this item. Please try again";
        setStatus(errorMessage);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(error, "We couldn't update this item. Please try again");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const conditions = [
    "NEW Retail",
    "New Bulk",
    "Refurbished",
    "Used",
    "AS-IS",
  ];

  const categories = [
    "Drones/UAV",
    "Motors & Props",
    "Cameras & Gimbals",
    "Batteries",
    "GCS – Ground Control Systems",
    "Storage",
    "Components",
    "Software",
    "C-UAS",
    "Services",
    "Other",
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Inventory Item</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          {formik.status && (
            <Alert variant="danger" className="mb-3">
              {formik.status}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label htmlFor="manufacturer" className="form-label">
                  Manufacturer *
                </label>
                <input
                  type="text"
                  id="manufacturer"
                  className={`form-control ${
                    formik.touched.manufacturer && formik.errors.manufacturer ? "is-invalid" : ""
                  }`}
                  {...formik.getFieldProps("manufacturer")}
                />
                {formik.touched.manufacturer && formik.errors.manufacturer && (
                  <div className="invalid-feedback">{formik.errors.manufacturer}</div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <label htmlFor="partNumber" className="form-label">
                  Part Number
                </label>
                <input
                  type="text"
                  id="partNumber"
                  className="form-control"
                  {...formik.getFieldProps("partNumber")}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label htmlFor="model" className="form-label">
                  Model
                </label>
                <input
                  type="text"
                  id="model"
                  className="form-control"
                  {...formik.getFieldProps("model")}
                />
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  className="form-control"
                  {...formik.getFieldProps("title")}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="quantity" className="form-label">
                  Quantity *
                </label>
                <input
                  type="number"
                  id="quantity"
                  className={`form-control ${
                    formik.touched.quantity && formik.errors.quantity ? "is-invalid" : ""
                  }`}
                  min="1"
                  {...formik.getFieldProps("quantity")}
                />
                {formik.touched.quantity && formik.errors.quantity && (
                  <div className="invalid-feedback">{formik.errors.quantity}</div>
                )}
              </div>
            </Col>

            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="condition" className="form-label">
                  Condition *
                </label>
                <select
                  id="condition"
                  className={`form-select ${
                    formik.touched.condition && formik.errors.condition ? "is-invalid" : ""
                  }`}
                  {...formik.getFieldProps("condition")}
                >
                  <option value="">Select condition</option>
                  {conditions.map((cond) => (
                    <option key={cond} value={cond}>
                      {cond}
                    </option>
                  ))}
                </select>
                {formik.touched.condition && formik.errors.condition && (
                  <div className="invalid-feedback">{formik.errors.condition}</div>
                )}
              </div>
            </Col>

            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="category" className="form-label">
                  Category
                </label>
                <select id="category" className="form-select" {...formik.getFieldProps("category")}>
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="price" className="form-label">
                  Price
                </label>
                <input
                  type="number"
                  id="price"
                  className={`form-control ${
                    formik.touched.price && formik.errors.price ? "is-invalid" : ""
                  }`}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  {...formik.getFieldProps("price")}
                />
                {formik.touched.price && formik.errors.price && (
                  <div className="invalid-feedback">{formik.errors.price}</div>
                )}
              </div>
            </Col>

            {/* [2026-02-13] CURRENCY_PHASE1: Currency is read-only, inherited from user profile */}
            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="currency" className="form-label">
                  Currency
                </label>
                <input
                  type="text"
                  id="currency"
                  className="form-control"
                  value={formik.values.currency}
                  readOnly
                />
              </div>
            </Col>

            <Col md={4}>
              <div className="mb-3">
                <label htmlFor="country" className="form-label">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  className="form-control"
                  placeholder="e.g., US, UK"
                  {...formik.getFieldProps("country")}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  className="form-control"
                  rows={3}
                  {...formik.getFieldProps("description")}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="isActive"
                    className="form-check-input"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                  <label htmlFor="isActive" className="form-check-label">
                    Active
                  </label>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn-cancel-figma"
            onClick={onHide}
            disabled={formik.isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-black"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting ? "Updating..." : "Update Item"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditItemModal;
